import { z } from 'zod';
import {
  emailSchema,
  organizationStatusSchema,
  userNameSchema,
  userStatusSchema,
  userTypeSchema,
} from './auth.js';
import { permissionSchema, roleScopeSchema } from './permissions.js';

/** ADM-01..03: request and response schemas of the admin area. */

export const timestampSchema = z.iso.datetime({ offset: true });

/** Cursor pagination: opaque cursor, `nextCursor` null on the last page. */
export const cursorQuerySchema = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export function pageSchema<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

export const idParamsSchema = z.object({ id: z.uuid() });

// --- Organizations (ADM-02) -------------------------------------------------------------------

export const organizationNameSchema = z.string().trim().min(1).max(200);

export const organizationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: organizationStatusSchema,
  notes: z.string().nullable(),
  userCount: z.number().int(),
  createdAt: timestampSchema,
});
export type Organization = z.infer<typeof organizationSchema>;

export const organizationListResponseSchema = z.object({ items: z.array(organizationSchema) });

export const createOrganizationBodySchema = z.object({
  name: organizationNameSchema,
  notes: z.string().trim().max(5000).nullable().optional(),
});
export type CreateOrganizationBody = z.infer<typeof createOrganizationBodySchema>;

export const updateOrganizationBodySchema = z.object({
  name: organizationNameSchema.optional(),
  status: organizationStatusSchema.optional(),
  notes: z.string().trim().max(5000).nullable().optional(),
});
export type UpdateOrganizationBody = z.infer<typeof updateOrganizationBodySchema>;

// --- Roles (ADM-03, PERM-01) -------------------------------------------------------------------

export const roleNameSchema = z.string().trim().min(1).max(100);

export const roleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  scope: roleScopeSchema,
  externallyAssignable: z.boolean(),
  isSystem: z.boolean(),
  permissions: z.array(permissionSchema),
  /** Global role assignments plus project memberships using this role. */
  assignmentCount: z.number().int(),
});
export type Role = z.infer<typeof roleSchema>;

export const roleListResponseSchema = z.object({ items: z.array(roleSchema) });

/**
 * Catalog and scope checks happen here; the rule that externally assignable roles contain only
 * externally allowed permissions is checked by the service as well (it must also hold for updates).
 */
export const createRoleBodySchema = z.object({
  name: roleNameSchema,
  description: z.string().trim().max(1000).nullable().optional(),
  scope: roleScopeSchema,
  externallyAssignable: z.boolean().default(false),
  permissions: z.array(permissionSchema).max(100),
});
export type CreateRoleBody = z.infer<typeof createRoleBodySchema>;

export const updateRoleBodySchema = z.object({
  name: roleNameSchema.optional(),
  description: z.string().trim().max(1000).nullable().optional(),
  externallyAssignable: z.boolean().optional(),
  permissions: z.array(permissionSchema).max(100).optional(),
});
export type UpdateRoleBody = z.infer<typeof updateRoleBodySchema>;

// --- Users (ADM-01) ----------------------------------------------------------------------------

export const adminUserListQuerySchema = cursorQuerySchema.extend({
  q: z.string().trim().max(200).optional(),
  type: userTypeSchema.optional(),
  status: userStatusSchema.optional(),
  organizationId: z.uuid().optional(),
});
export type AdminUserListQuery = z.infer<typeof adminUserListQuerySchema>;

export const adminUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  userType: userTypeSchema,
  status: userStatusSchema,
  breakGlass: z.boolean(),
  organization: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  lastLoginAt: timestampSchema.nullable(),
  /** Profile picture (ADM-07); null without one. */
  avatarUrl: z.string().nullable(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const adminUserListResponseSchema = pageSchema(adminUserSchema);

export const adminUserDetailSchema = adminUserSchema.extend({
  twoFactorEnabled: z.boolean(),
  createdAt: timestampSchema,
  globalRoles: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;

export const setGlobalRolesBodySchema = z.object({
  roleIds: z.array(z.uuid()).max(50),
});
export type SetGlobalRolesBody = z.infer<typeof setGlobalRolesBodySchema>;

/** POST /api/v1/admin/users/invitations – invite (or re-invite) an external user (AUTH-03). */
export const inviteUserBodySchema = z.object({
  organizationId: z.uuid(),
  email: emailSchema,
  name: userNameSchema,
});
export type InviteUserBody = z.infer<typeof inviteUserBodySchema>;

/** POST /api/v1/admin/users/internal – create an internal user in advance (AUTH-02). */
export const createInternalUserBodySchema = z.object({
  email: emailSchema,
  name: userNameSchema,
});
export type CreateInternalUserBody = z.infer<typeof createInternalUserBodySchema>;

/** GET /api/v1/admin/users/:id/memberships – project memberships visible to the caller. */
export const userMembershipSchema = z.object({
  project: z.object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    archived: z.boolean(),
  }),
  role: z.object({ id: z.uuid(), name: z.string() }),
  /** The caller may change or remove this membership (project.manage_members, not archived). */
  canManage: z.boolean(),
});
export type UserMembership = z.infer<typeof userMembershipSchema>;
export const userMembershipListResponseSchema = z.object({
  items: z.array(userMembershipSchema),
});
export type UserMembershipListResponse = z.infer<typeof userMembershipListResponseSchema>;

/** `link` only when the e-mail could not be sent, so the admin can pass it on manually. */
export const inviteUserResponseSchema = z.object({
  userId: z.uuid(),
  mailSent: z.boolean(),
  link: z.string().nullable(),
});
export type InviteUserResponse = z.infer<typeof inviteUserResponseSchema>;

/** Problem `type` URIs of the admin area, so the UI can show specific messages. */
export const adminProblemTypes = {
  roleNotExternallyAssignable: 'urn:pm-tool:problem:role-not-externally-assignable',
  roleInUse: 'urn:pm-tool:problem:role-in-use',
  roleAssignedToExternals: 'urn:pm-tool:problem:role-assigned-to-externals',
  systemRole: 'urn:pm-tool:problem:system-role',
  permissionScopeMismatch: 'urn:pm-tool:problem:permission-scope-mismatch',
  nameTaken: 'urn:pm-tool:problem:name-taken',
  organizationInUse: 'urn:pm-tool:problem:organization-in-use',
  organizationInactive: 'urn:pm-tool:problem:organization-inactive',
  externalGlobalRole: 'urn:pm-tool:problem:external-global-role',
  selfLockout: 'urn:pm-tool:problem:self-lockout',
  /** CSV export of more than AUDIT_EXPORT_MAX_ROWS entries; narrow the filters. */
  auditExportTooLarge: 'urn:pm-tool:problem:audit-export-too-large',
} as const;
